import { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { HiOutlineCalendar, HiOutlineBuildingLibrary, HiOutlineBuildingOffice2, HiOutlineChatBubbleLeftEllipsis, HiOutlineLightBulb, HiOutlineNewspaper, HiOutlinePhoto, HiOutlineTrophy } from 'react-icons/hi2'

const API_URL = import.meta.env.VITE_API_URL

// Helper: shuffle array
function shuffle(array) {
  return array
    .map(value => ({ value, sort: Math.random() }))
    .sort((a, b) => a.sort - b.sort)
    .map(({ value }) => value)
}

function ExhibitionsAwards() {
  const { t, i18n } = useTranslation()
  const [awards, setAwards] = useState([])
  const [exhibitions, setExhibitions] = useState([])
  const [paintings, setPaintings] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  // Collage state for awards and exhibitions
  const [awardCollages, setAwardCollages] = useState({})
  const [exhibitionCollages, setExhibitionCollages] = useState({})
  const [modalImage, setModalImage] = useState(null)
  const [modalImages, setModalImages] = useState([])
  const [modalIdx, setModalIdx] = useState(0)

  // Keyboard navigation for modal
  const handleModalKeyDown = useCallback((e) => {
    if (!modalImages.length) return;
    if (e.key === 'ArrowLeft') {
      setModalIdx(idx => (idx - 1 + modalImages.length) % modalImages.length)
    } else if (e.key === 'ArrowRight') {
      setModalIdx(idx => (idx + 1) % modalImages.length)
    } else if (e.key === 'Escape') {
      setModalImage(null); setModalImages([]); setModalIdx(0);
    }
  }, [modalImages])

  useEffect(() => {
    if (modalImage && modalImages.length) {
      window.addEventListener('keydown', handleModalKeyDown)
      return () => window.removeEventListener('keydown', handleModalKeyDown)
    }
  }, [modalImage, modalImages, handleModalKeyDown])

  useEffect(() => {
    Promise.all([
      fetch(`${API_URL}/awards`).then(res => res.json()),
      fetch(`${API_URL}/exhibitions`).then(res => res.json()),
      fetch(`${API_URL}/paintings`).then(res => res.json())
    ])
      .then(([awardsData, exhibitionsData, paintingsData]) => {
        // Sort awards by date (newest first)
        const sortedAwards = (awardsData || []).sort((a, b) => {
          const dateA = a.date ? new Date(a.date) : new Date(0);
          const dateB = b.date ? new Date(b.date) : new Date(0);
          return dateB - dateA; // Newest first
        });
        setAwards(sortedAwards)
        
        // Sort exhibitions by start_date (newest first)
        const sortedExhibitions = (exhibitionsData || []).sort((a, b) => {
          const dateA = a.start_date ? new Date(a.start_date) : new Date(0);
          const dateB = b.start_date ? new Date(b.start_date) : new Date(0);
          return dateB - dateA; // Newest first
        });
        setExhibitions(sortedExhibitions)
        setPaintings(paintingsData || [])
        setLoading(false)
      })
      .catch(err => {
        setError(err.message)
        setLoading(false)
      })
  }, [])

  // Helper: get main image src from painting.images
  const getMainImageSrc = (painting) => {
    if (!painting || !painting.images || painting.images.length === 0) return null
    const mainImg = painting.images.find(img => img.is_main) || painting.images[0]
    return mainImg ? `${API_URL}${mainImg.thumb_path || mainImg.file_path}` : null
  }

  // Helper: get main images from related paintings for collage
  const getCollageImages = (paintings, max = 4) => {
    if (!paintings || paintings.length === 0) return []
    const mainImages = paintings.flatMap(p =>
      (p.images || [])
        .filter(img => img.is_main)
        .map(img => ({
          thumbSrc: `${API_URL}${img.thumb_path || img.file_path}`,
          fullSrc: `${API_URL}${img.file_path}`,
          alt: p.name || p.title,
          paintingId: p.id,
        }))
    )
    return shuffle(mainImages).slice(0, max)
  }

  // Build relatedPaintings for each award/exhibition
  const getRelatedPaintingsForAward = (award) => {
    if (!award || !award.id) return []
    return paintings.filter(p => Array.isArray(p.award_ids) && p.award_ids.includes(award.id))
  }
  const getRelatedPaintingsForExhibition = (exhibition) => {
    if (!exhibition || !exhibition.id) return []
    return paintings.filter(p => Array.isArray(p.exhibition_ids) && p.exhibition_ids.includes(exhibition.id))
  }

  // Persist collage images for awards and exhibitions only on data load
  useEffect(() => {
    if (awards.length && paintings.length) {
      const newCollages = {}
      awards.forEach(award => {
        const relatedPaintings = getRelatedPaintingsForAward(award)
        newCollages[award.id] = getCollageImages(relatedPaintings, 4)
      })
      setAwardCollages(newCollages)
    }
  }, [awards, paintings])

  useEffect(() => {
    if (exhibitions.length && paintings.length) {
      const newCollages = {}
      exhibitions.forEach(exhibition => {
        const relatedPaintings = getRelatedPaintingsForExhibition(exhibition)
        newCollages[exhibition.id] = getCollageImages(relatedPaintings, 4)
      })
      setExhibitionCollages(newCollages)
    }
  }, [exhibitions, paintings])

  // Helper: format date
  function formatDate(dateStr) {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (isNaN(d)) return dateStr;
    return d.toLocaleDateString(i18n.language === 'de' ? 'de-DE' : 'en-US', { year: 'numeric', month: '2-digit', day: '2-digit' });
  }

  // Helper: format month + year
  function formatMonthYear(dateStr) {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (isNaN(d)) return dateStr;
    return d.toLocaleDateString(i18n.language === 'de' ? 'de-DE' : 'en-US', { year: 'numeric', month: 'long' });
  }

  if (loading) return (
    <main className="flex-1 w-screen min-h-[400px] flex items-center justify-center bg-gray-100 dark:bg-zinc-900">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-900 dark:border-gray-100"></div>
    </main>
  )

  if (error) return (
    <main className="flex-1 w-screen min-h-[400px] flex items-center justify-center bg-gray-100 dark:bg-zinc-900">
      <div className="text-center text-red-500 dark:text-red-400 p-8">
        {t('error.loading')}
      </div>
    </main>
  )

  return (
    <div className="min-h-screen w-screen bg-white dark:bg-zinc-900 transition-colors duration-300">
      {/* Fullscreen Modal for Collage Images */}
      {modalImage && modalImages.length > 0 && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-90" onClick={() => { setModalImage(null); setModalImages([]); setModalIdx(0); }}>
          <div className="relative w-full h-full flex items-center justify-center">
            <img
              src={modalImages[modalIdx]?.src}
              alt={modalImages[modalIdx]?.alt || 'Collage Fullscreen'}
              className="w-full h-full object-contain"
            />
            <button
              className="absolute top-4 right-4 p-2 rounded border border-gray-300 dark:border-zinc-700 bg-white/80 dark:bg-zinc-800/80 shadow text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-zinc-700 focus:outline-none focus:ring-2 focus:ring-blue-400 z-[10000] text-2xl font-bold"
              onClick={e => { e.stopPropagation(); setModalImage(null); setModalImages([]); setModalIdx(0); }}
              aria-label="Schließen"
            >
              ✕
            </button>
            {/* Prev Button */}
            {modalImages.length > 1 && (
              <button
                className="absolute left-2 md:left-8 top-1/2 -translate-y-1/2 text-3xl text-white bg-black/40 rounded-full px-2 py-1 z-50 hover:bg-black/70"
                onClick={e => { e.stopPropagation(); setModalIdx(idx => (idx - 1 + modalImages.length) % modalImages.length); }}
                aria-label="Vorheriges Bild"
              >
                ‹
              </button>
            )}
            {/* Next Button */}
            {modalImages.length > 1 && (
              <button
                className="absolute right-2 md:right-8 top-1/2 -translate-y-1/2 text-3xl text-white bg-black/40 rounded-full px-2 py-1 z-50 hover:bg-black/70"
                onClick={e => { e.stopPropagation(); setModalIdx(idx => (idx + 1) % modalImages.length); }}
                aria-label="Nächstes Bild"
              >
                ›
              </button>
            )}
          </div>
        </div>
      )}
      {/* Awards Section */}
      <section className="w-full py-12 px-4 md:px-12 lg:px-24">
        <div className="w-full max-w-5xl mx-auto">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            {t('awards.title')}
          </h1>
          <div className="h-px w-16 bg-gray-300 dark:bg-zinc-700 mb-8" />
          {awards.length === 0 ? (
            <div className="text-center p-8">
              <p className="text-gray-500 mb-4 text-lg dark:text-gray-400">{t("no_awards")}</p>
              <p className="text-sm text-gray-400">{t("add_first_award")}</p>
            </div>
          ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {awards.map((award) => {
              const relatedPaintings = getRelatedPaintingsForAward(award)
              const collageImages = awardCollages[award.id] || []
              return (
                <Link
                  key={award.id}
                  to={`/awards/${award.id}`}
                  className="relative bg-white dark:bg-zinc-800 border-2 border-yellow-400 dark:border-yellow-600 rounded-xl shadow-md hover:shadow-lg transition-shadow duration-200 p-6 flex flex-col gap-2 items-start text-left focus:outline-none focus:ring-2 focus:ring-yellow-400 dark:focus:ring-yellow-500"
                  style={{ textDecoration: 'none' }}
                  tabIndex={0}
                >
                  {/* Award Badge */}
                  <span className="absolute top-3 right-3 bg-yellow-400 text-white text-xs font-bold px-2 py-0.5 rounded-full flex items-center gap-1 shadow">
                    <HiOutlineTrophy className="inline-block" /> Award
                  </span>
                  {/* Mobile: Compact Portrait Collage */}
                  <div className="grid grid-cols-2 gap-1 sm:hidden">
                    {collageImages.slice(0, 4).map((img, idx) => (
                      <div
                        key={idx}
                        className="aspect-[3/4] w-full rounded-lg shadow-md border border-white dark:border-zinc-700 overflow-hidden bg-gray-200 dark:bg-zinc-700 cursor-pointer"
                        onClick={e => { e.preventDefault(); e.stopPropagation(); setModalImage(img.fullSrc); setModalImages(collageImages.slice(0, 4).map(i => ({ ...i, src: i.fullSrc }))); setModalIdx(idx); }}
                        role="button"
                        tabIndex={0}
                        aria-label="Bild vergrößern"
                      >
                        <img src={img.thumbSrc} alt={img.alt || t('gallery.painting_image')} className="w-full h-full object-cover" />
                      </div>
                    ))}
                  </div>
                  {/* Desktop: Subtle Collage */}
                  <div className="hidden sm:block relative h-48 w-full">
                    {collageImages.slice(0, 4).map((img, idx) => (
                      <div
                        key={idx}
                        className={`
                          absolute rounded-lg shadow-md border-2 border-white dark:border-zinc-700
                          cursor-pointer
                          ${idx === 0 ? 'left-0 top-2 w-1/3 h-32 rotate-1 z-10 hover:z-20' : ''}
                          ${idx === 1 ? 'left-1/4 top-8 w-1/3 h-32 -rotate-2 z-30' : ''}
                          ${idx === 2 ? 'left-1/2 top-4 w-1/3 h-32 rotate-2 z-10 hover:z-20' : ''}
                          ${idx === 3 ? 'left-1/3 top-20 w-1/4 h-20 -rotate-1 z-0 hover:z-10' : ''}
                        `}
                        onClick={e => { e.preventDefault(); e.stopPropagation(); setModalImage(img.fullSrc); setModalImages(collageImages.slice(0, 4).map(i => ({ ...i, src: i.fullSrc }))); setModalIdx(idx); }}
                        role="button"
                        tabIndex={0}
                        aria-label="Bild vergrößern"
                      >
                        <img
                          src={img.thumbSrc}
                          alt={img.alt || t('gallery.painting_image')}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ))}
                  </div>
                  {/* NEUES DESIGN: Infos auf der Card */}
                  {/* Titel */}
                  <h3 className="font-bold text-xl md:text-2xl text-gray-900 dark:text-gray-100 mb-1 mt-2">{award.title || award.name}</h3>
                  {/* Datum & Ort */}
                  <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500 dark:text-gray-400 mb-1">
                    {award.date && (
                      <span className="flex items-center gap-1"><HiOutlineCalendar className="inline-block text-base align-middle" /> {formatMonthYear(award.date)}</span>
                    )}
                    {!award.date && award.year && (
                      <span className="flex items-center gap-1"><HiOutlineCalendar className="inline-block text-base align-middle" /> {award.year}</span>
                    )}
                    {award.location && (
                      <span className="flex items-center gap-1"><HiOutlineBuildingLibrary className="inline-block text-base align-middle" /> {award.location}</span>
                    )}
                  </div>
                  {/* Institution */}
                  {award.institution && (
                    <div className="italic text-xs text-gray-500 dark:text-gray-400 mb-1 flex items-center gap-1">
                      <HiOutlineBuildingOffice2 className="inline-block text-base align-middle" /> {award.institution}
                    </div>
                  )}
                  {/* Beschreibung */}
                  {award.description && (
                    <p className="text-gray-700 dark:text-gray-300 text-sm mb-2 line-clamp-3">{award.description}</p>
                  )}
                  {/* Jury-Statement */}
                  {award.jury_statement && (
                    <blockquote className="italic text-xs text-gray-600 dark:text-gray-400 border-l-4 border-gray-200 dark:border-zinc-700 pl-3 py-2 bg-gray-50 dark:bg-zinc-800 rounded mb-2 flex items-center gap-2">
                      <HiOutlineChatBubbleLeftEllipsis className="inline-block text-base align-middle" /> "{award.jury_statement}"
                    </blockquote>
                  )}
                  {/* Offizieller Link */}
                  {award.official_link && (
                    <a href={award.official_link} target="_blank" rel="noopener noreferrer" className="underline text-xs text-blue-700 dark:text-blue-300 hover:text-blue-900 dark:hover:text-blue-100 mb-2">
                      {t('awards.official_link')}
                    </a>
                  )}
                  {/* Zugehörige Werke */}
                  {relatedPaintings.length > 0 && (
                    <div className="mt-3 w-full">
                      <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-200 mb-1 flex items-center gap-1">
                        <HiOutlinePhoto className="inline-block text-base align-middle" /> {t('awards.relatedPaintings')}
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {relatedPaintings.map((painting) => {
                          const thumbSrc = getMainImageSrc(painting)
                          return (
                            <Link
                              key={painting.id}
                              to={`/gallery/${painting.id}`}
                              className="flex items-center gap-1 underline text-sm text-gray-700 dark:text-gray-200 hover:text-black dark:hover:text-white transition"
                              onClick={e => e.stopPropagation()}
                            >
                              {thumbSrc && (
                                <img src={thumbSrc} alt={painting.title || painting.name} className="w-7 h-7 object-cover rounded border border-gray-200 dark:border-zinc-700" />
                              )}
                              {painting.name || painting.title}
                            </Link>
                          )
                        })}
                      </div>
                    </div>
                  )}
                </Link>
              )
            })}
          </div>
          )}
        </div>
      </section>

      {/* Exhibitions Section */}
      <section className="w-full py-12 px-4 md:px-12 lg:px-24 bg-gray-50 dark:bg-zinc-800">
        <div className="w-full max-w-5xl mx-auto">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            {t('exhibitions.title')}
          </h1>
          <div className="h-px w-16 bg-gray-300 dark:bg-zinc-700 mb-8" />
          {exhibitions.length === 0 ? (
            <div className="text-center p-8">
              <p className="text-gray-500 mb-4 text-lg dark:text-gray-400">{t("no_exhibitions")}</p>
              <p className="text-sm text-gray-400">{t("add_first_exhibition")}</p>
            </div>
          ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {exhibitions.map((exhibition) => {
              const relatedPaintings = getRelatedPaintingsForExhibition(exhibition)
              const collageImages = exhibitionCollages[exhibition.id] || []
              return (
                <Link
                  key={exhibition.id}
                  to={`/exhibitions/${exhibition.id}`}
                  className="relative bg-white dark:bg-zinc-800 border-2 border-blue-400 dark:border-blue-600 rounded-xl shadow-md hover:shadow-lg transition-shadow duration-200 p-6 flex flex-col gap-2 items-start text-left focus:outline-none focus:ring-2 focus:ring-blue-400 dark:focus:ring-blue-500"
                  style={{ textDecoration: 'none' }}
                  tabIndex={0}
                >
                  {/* Exhibition Badge */}
                  <span className="absolute top-3 right-3 bg-blue-400 text-white text-xs font-bold px-2 py-0.5 rounded-full flex items-center gap-1 shadow">
                    <HiOutlineBuildingLibrary className="inline-block" /> Exhibition
                  </span>
                  {/* Mobile: Compact Portrait Collage */}
                  <div className="grid grid-cols-2 gap-1 sm:hidden">
                    {collageImages.slice(0, 4).map((img, idx) => (
                      <div
                        key={idx}
                        className="aspect-[3/4] w-full rounded-lg shadow-md border border-white dark:border-zinc-700 overflow-hidden bg-gray-200 dark:bg-zinc-700 cursor-pointer"
                        onClick={e => { e.preventDefault(); e.stopPropagation(); setModalImage(img.fullSrc); setModalImages(collageImages.slice(0, 4).map(i => ({ ...i, src: i.fullSrc }))); setModalIdx(idx); }}
                        role="button"
                        tabIndex={0}
                        aria-label="Bild vergrößern"
                      >
                        <img src={img.thumbSrc} alt={img.alt || t('gallery.painting_image')} className="w-full h-full object-cover" />
                      </div>
                    ))}
                  </div>
                  {/* Desktop: Subtle Collage */}
                  <div className="hidden sm:block relative h-48 w-full">
                    {collageImages.slice(0, 4).map((img, idx) => (
                      <div
                        key={idx}
                        className={`
                          absolute rounded-lg shadow-md border-2 border-white dark:border-zinc-700
                          cursor-pointer
                          ${idx === 0 ? 'left-0 top-2 w-1/3 h-32 rotate-1 z-10 hover:z-20' : ''}
                          ${idx === 1 ? 'left-1/4 top-8 w-1/3 h-32 -rotate-2 z-30' : ''}
                          ${idx === 2 ? 'left-1/2 top-4 w-1/3 h-32 rotate-2 z-10 hover:z-20' : ''}
                          ${idx === 3 ? 'left-1/3 top-20 w-1/4 h-20 -rotate-1 z-0 hover:z-10' : ''}
                        `}
                        onClick={e => { e.preventDefault(); e.stopPropagation(); setModalImage(img.fullSrc); setModalImages(collageImages.slice(0, 4).map(i => ({ ...i, src: i.fullSrc }))); setModalIdx(idx); }}
                        role="button"
                        tabIndex={0}
                        aria-label="Bild vergrößern"
                      >
                        <img
                          src={img.thumbSrc}
                          alt={img.alt || t('gallery.painting_image')}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ))}
                  </div>
                  {/* NEUES DESIGN: Infos auf der Card */}
                  {/* Titel */}
                  <h3 className="font-bold text-xl md:text-2xl text-gray-900 dark:text-gray-100 mb-1 mt-2">{exhibition.title || exhibition.name}</h3>
                  {/* Datum & Ort */}
                  <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500 dark:text-gray-400 mb-1">
                    {(exhibition.start_date || exhibition.end_date) && (
                      <span className="flex items-center gap-1"><HiOutlineCalendar className="inline-block text-base align-middle" /> {formatDate(exhibition.start_date)}{exhibition.end_date ? ` – ${formatDate(exhibition.end_date)}` : ''}</span>
                    )}
                    {(
                      exhibition.location ||
                      (Array.isArray(exhibition.locations) && exhibition.locations.length > 0) ||
                      (typeof exhibition.locations === 'string' && exhibition.locations.trim() !== '')
                    ) && (
                      <span className="flex items-center gap-1"><HiOutlineBuildingLibrary className="inline-block text-base align-middle" /> {
                        [
                          exhibition.location,
                          ...(Array.isArray(exhibition.locations)
                            ? exhibition.locations
                            : (typeof exhibition.locations === 'string' && exhibition.locations.trim() !== ''
                              ? [exhibition.locations]
                              : [])
                          )
                        ]
                          .filter(Boolean)
                          .join(', ')
                      }</span>
                    )}
                  </div>
                  {/* Veranstalter */}
                  {exhibition.organizer && (
                    <div className="italic text-xs text-gray-500 dark:text-gray-400 mb-1 flex items-center gap-1">
                      <HiOutlineBuildingOffice2 className="inline-block text-base align-middle" /> {exhibition.organizer}
                    </div>
                  )}
                  {/* Beschreibung */}
                  {exhibition.description && (
                    <p className="text-gray-700 dark:text-gray-300 text-sm mb-2 line-clamp-3">{exhibition.description}</p>
                  )}
                  {/* Konzept */}
                  {exhibition.concept && (
                    <blockquote className="italic text-xs text-gray-600 dark:text-gray-400 border-l-4 border-gray-200 dark:border-zinc-700 pl-3 py-2 bg-gray-50 dark:bg-zinc-800 rounded mb-2 flex items-center gap-2">
                      <HiOutlineLightBulb className="inline-block text-base align-middle" /> {exhibition.concept}
                    </blockquote>
                  )}
                  {/* Presselinks */}
                  {exhibition.press_links && exhibition.press_links.length > 0 && (
                    <div className="mb-2 mt-2">
                      <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-200 mb-1 flex items-center gap-1"><HiOutlineNewspaper className="inline-block text-base align-middle" /> {t('exhibitions.press_links')}</h4>
                      <ul className="list-disc list-inside">
                        {exhibition.press_links.map((link, idx) => (
                          <li key={idx}>
                            <a href={link.url} target="_blank" rel="noopener noreferrer" className="underline text-xs text-blue-700 dark:text-blue-300 hover:text-blue-900 dark:hover:text-blue-100">
                              {link.title || link.url}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {/* Zugehörige Werke */}
                  {relatedPaintings.length > 0 && (
                    <div className="mt-3 w-full">
                      <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-200 mb-1 flex items-center gap-1">
                        <HiOutlinePhoto className="inline-block text-base align-middle" /> {t('exhibitions.relatedPaintings')}
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {relatedPaintings.map((painting) => {
                          const thumbSrc = getMainImageSrc(painting)
                          return (
                            <Link
                              key={painting.id}
                              to={`/gallery/${painting.id}`}
                              className="flex items-center gap-1 underline text-sm text-gray-700 dark:text-gray-200 hover:text-black dark:hover:text-white transition"
                              onClick={e => e.stopPropagation()}
                            >
                              {thumbSrc && (
                                <img src={thumbSrc} alt={painting.title || painting.name} className="w-7 h-7 object-cover rounded border border-gray-200 dark:border-zinc-700" />
                              )}
                              {painting.name || painting.title}
                            </Link>
                          )
                        })}
                      </div>
                    </div>
                  )}
                </Link>
              )
            })}
          </div>
          )}
        </div>
      </section>
    </div>
  )
}

export default ExhibitionsAwards 